import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'
import { AngularSplitModule } from 'angular-split'

import { take } from 'rxjs'
import { fromInteropObservable } from 'rxjs/internal/observable/innerFrom'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxModalData,
  SelectItem,
} from '@flowx/angular-ui-toolkit'
import {
  FixCodeType,
  FixCodeWarningsContext,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import { AIContentType } from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/common.types'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import {
  CodeEditorFlxAutocompleteOptions,
  CodeEditorFlxLanguage,
} from '@flowx/designer/modules/monaco-code-editor/types'
import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'
import { extractReusableFunctionsInScript } from '@flowx/designer/modules/reusable-resources/reusable-functions/models/reusable-function.entity'
import { DataModelTypingsBuilderService } from '@flowx/designer/modules/script-builder/components/code-editor/data-model-typings-builder.service'
import { PreviewMode } from '@flowx/designer/modules/script-builder/constants/expressions.constants'
import { TestModalComponent } from '@flowx/designer/shared/components/test-modal/test-modal.component'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  reusableFunctionsWithAppNameMap,
  selectJavascriptReusableFunctions,
} from '@flowx/designer/shared/store/selectors/reusable-functions.selectors'
import { isSelectItem } from '@flowx/shared/utils'

import { ExpressionEvalService } from '../../services/expression-eval.service'
import { ExpressionEditorOutput } from '../expression-editor/expression-editor.component'
import {
  RunDataEmitterPayload,
  SaveDataEmitterPayload,
  ScriptingEditComponent,
} from '../scripting-edit/scripting-edit.component'
import { ScriptingInputDataComponent } from '../scripting-input-data/scripting-input-data.component'
import {
  OutcomeError,
  ScriptingOutputComponent,
} from '../scripting-output/scripting-output.component'

@Component({
  selector: 'flx-business-rule-editor',
  imports: [
    AngularSplitModule,
    ScriptingInputDataComponent,
    ScriptingEditComponent,
    ScriptingOutputComponent,
    TestModalComponent,
    FlxButtonModule,
  ],
  templateUrl: './business-rule-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'w-full h-full' },
})
export class BusinessRuleEditorModalComponent implements OnInit, OnDestroy {
  // TODO Add more specific type to #data
  #data: FlxModalData = inject(FLX_MODAL_DATA)
  #expressionEvalService = inject(ExpressionEvalService)
  #toastMessageService = inject(ToastMessageService)
  #destroyRef = inject(DestroyRef)
  #store = inject(Store)

  #reusableFunctionsWithAppNameMap = this.#store.selectSignal(reusableFunctionsWithAppNameMap)
  #allJavascriptReusableFunctions = this.#store.selectSignal(selectJavascriptReusableFunctions)
  #dataModelTypingsBuilderService = inject(DataModelTypingsBuilderService)

  @Output() dataEventEmitter = new EventEmitter<ExpressionEditorOutput>()

  designerAiAccess = inject(ApplicationPermissionsStore).designerAiAccess

  readonly autoCompleteOptions: Partial<CodeEditorFlxAutocompleteOptions> = {
    [CodeEditorFlxLanguage.Javascript]: {
      interpolationAutocomplete: false,
      inputObjectAutocomplete: false,
      typingsAutocomplete: {
        showInputTypings: true,
        showOutputTypings: true,
        showFunctions: true,
        showConfigurationParams: true,
      },
    },

    [CodeEditorFlxLanguage.Java]: {
      interpolationAutocomplete: false,
      inputObjectAutocomplete: true,
    },
  }

  expression = signal<string>(null)
  language = signal<ScriptingLanguage | SelectItem<ScriptingLanguage>>(null)
  supportsGraalVM = signal<boolean>(false)
  useGraalVM = signal<boolean>(false)
  processDefinitionId = signal<ProcessDefinition['id']>(null)
  processDefinitionName = signal<ProcessDefinition['resourceName']>(null)
  withFixWarnings = signal<boolean>(false)

  languageValue = computed(() => {
    const language = this.language()
    return isSelectItem(language) ? language.value : language
  })

  input = signal<string>('{}')
  output = signal<unknown>(null)
  outputIsComputed = signal(false)
  error = signal<OutcomeError>(null)
  readonly = toSignal(fromInteropObservable<boolean>(BpmnEditorStores.readonly$))
  conversationTargetDescriptor = signal<{ nodeId: number; actionId: number; index: number }>(null)

  // TODO: rewire to the AI builder harness, alongside the parked loader overlay in the template.
  fixLoaderTitle = computed(() => '')
  previewMode = signal<PreviewMode>('none')
  sections = {
    leftSection: {
      title: 'Input Data',
      subtitle: 'Choose a process instance or type the input data you want to test',
    },
    middleSection: { title: 'Edit Rule', subtitle: 'Write or build an expression' },
    rightSection: {
      title: 'Output',
      subtitle: 'Result of the input data being processed through the rule',
    },
  }

  nodeId = this.#store.selectSignal(nodesSelector)

  constructor() {
    effect(() => this.dataEventEmitter.emit({ updatedData: this.expression() }))

    effect(() => {
      const all = this.#allJavascriptReusableFunctions()
      const ruleUseGraalVM = this.useGraalVM()
      this.#dataModelTypingsBuilderService.setReusableFunctionSource(
        all.filter((fn) => (fn.useGraalVM ?? false) === ruleUseGraalVM)
      )
    })
  }

  ngOnDestroy(): void {
    this.#dataModelTypingsBuilderService.setReusableFunctionSource(
      this.#allJavascriptReusableFunctions()
    )
  }

  ngOnInit(): void {
    this.expression.set(this.#data?.expression ?? '')
    this.language.set(this.#data?.language ?? null)
    this.supportsGraalVM.set(this.#data?.supportsGraalVM ?? false)
    this.useGraalVM.set(this.#data?.useGraalVM ?? false)
    this.processDefinitionId.set(this.#data?.processVersionId ?? null)
    this.processDefinitionName.set(this.#data?.processDefinitionName ?? '')
    this.withFixWarnings.set(this.#data?.withFixWarnings ?? false)
    this.conversationTargetDescriptor.set(this.#data.conversationTargetDescriptor)
  }

  // TODO: rewire to the AI builder harness. Cancels a fix that is still running — distinct from
  // keep/discard, which acted on a finished outcome and are not coming back.
  cancelFix(): void {}

  onSave(payload: SaveDataEmitterPayload): void {
    this.dataEventEmitter.emit({
      saveData: payload.expression,
      errors: payload.errors,
      language: payload.language,
      useGraalVM: this.useGraalVM(),
    })
  }

  onRun(payload: RunDataEmitterPayload): void {
    this.error.set(null)
    this.output.set(null)
    this.outputIsComputed.set(false)

    let inputObject = null

    try {
      inputObject = JSON.parse(this.input())
    } catch (e) {
      this.error.set({ msg: "Couldn't parse input" })
    }

    if (!inputObject) {
      return
    }

    try {
      this.#expressionEvalService
        .evalRemote(
          this.processDefinitionId(),
          {
            expression: payload.expression,
            language: payload.language ?? this.languageValue(),
            businessRuleReferences: extractReusableFunctionsInScript(
              payload.expression,
              this.#reusableFunctionsWithAppNameMap()
            ),
            useGraalVM: this.useGraalVM(),
          },
          inputObject
        )
        .pipe(take(1))
        .subscribe({
          next: (output) => {
            this.output.set(output)
            this.outputIsComputed.set(true)
            this.#toastMessageService.showSuccessMessage('Expression evaluated')
          },
          error: (e) => {
            this.error.set({ msg: e.error.detail })
          },
        })
    } catch (e) {
      this.error.set({ msg: e.detail })
    }
  }

  // TODO: rewire to the AI builder harness
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onFixError(errorMessage: string): void {}

  onClose(): void {
    this.dataEventEmitter.emit({ closing: true })
  }

  onPreviewModeChange(mode: PreviewMode): void {
    this.previewMode.set(mode)
  }

  #packageContext(errorMessage: string): FixCodeWarningsContext {
    const dataModelEntities = this.#store.selectSignal(selectDataModelEntitiesArray)()

    return {
      code: this.expression(),
      language: this.languageValue(),
      warnings: [{ message: errorMessage }] as any,
      datasource: dataModelEntities,
      fixCodeType: FixCodeType.Error,
      aiContentType: AIContentType.BusinessRule,
    }
  }
}
